import { scaleLinear } from 'd3-scale';
import { useMemo } from 'react';
import type { Day } from '@map-my-money/engine';
import { t } from '../../i18n/index.ts';
import { dayMonth, money } from '../../lib/format.ts';
import { useWidth } from '../../lib/hooks.ts';
import type { MonthView } from '../../lib/model.ts';
import { clamp } from './chartUtils.ts';
import { ScrubOverlay, useScrub } from './useScrub.ts';

const DOT: Record<string, string> = {
  income: 'var(--mint)',
  expense: 'var(--amber)',
  debt: 'var(--terracotta-text)',
  investment: 'var(--violet)',
};

/**
 * "This month": cash as a step line from the 1st. Confirmed days are solid, the rest of the
 * month is dashed and shaded, and every dated payment is a dot in its type's colour.
 */
export function MonthChart({ month, height = 128 }: { month: MonthView; height?: number }) {
  const [ref, width] = useWidth<HTMLDivElement>();
  const margin = { top: 26, right: 12, bottom: 22, left: 12 };
  const innerW = Math.max(0, width - margin.left - margin.right);
  const innerH = height - margin.top - margin.bottom;

  const layout = useMemo(() => {
    if (innerW <= 0 || month.points.length === 0) return null;
    const values = month.points.map((p) => p.value);
    const lo = Math.min(...values);
    const hi = Math.max(...values);
    const pad = Math.max((hi - lo) * 0.14, Math.abs(hi) * 0.02, 50);
    // Half a day of margin each side, so a payment on the 1st is a visible step from the opening balance.
    const x = scaleLinear().domain([month.periodStart - 0.6, month.periodEnd + 0.4]).range([0, innerW]);
    const y = scaleLinear().domain([lo - pad, hi + pad]).range([innerH, 0]);
    const confirmed = month.points.filter((p) => !p.projected);
    const todayPoint = confirmed[confirmed.length - 1] ?? month.points[0]!;
    const projected = [todayPoint, ...month.points.filter((p) => p.projected)];

    const stepPath = (pts: readonly { date: number; value: number }[], endX: number, from?: number) => {
      if (pts.length === 0) return '';
      let d = `M${from ?? x(pts[0]!.date)},${y(pts[0]!.value)}`;
      for (let i = 1; i < pts.length; i++) {
        d += ` H${x(pts[i]!.date)} V${y(pts[i]!.value)}`;
      }
      return `${d} H${endX}`;
    };
    const confirmedPath = stepPath(confirmed, x(todayPoint.date), 0);
    const projectedPath = stepPath(projected, innerW);
    const areaPath = `${projectedPath} V${innerH} H${x(todayPoint.date)} Z`;
    const dots = month.points
      .filter((p) => p.labels.length > 0)
      .map((p) => ({ p, cx: x(p.date), cy: y(p.value), color: DOT[p.labels[0]!.type] ?? 'var(--cyber)' }));
    const xs = month.points.map((p) => x(p.date));
    const dayTicks = [0, 7, 14, 21].map((d) => month.periodStart + d).concat([month.periodEnd]) as Day[];
    return { x, y, confirmedPath, projectedPath, areaPath, dots, todayX: x(month.today), endY: y(month.endValue), xs, dayTicks };
  }, [month, innerW, innerH]);

  const { index, handlers } = useScrub(layout?.xs ?? []);
  const active = index !== null ? month.points[index] : undefined;
  const summary = t('home.thisMonth.chart', {
    start: money(month.startValue), startDate: dayMonth(month.periodStart), today: money(month.todayValue),
    end: money(month.endValue), endDate: dayMonth(month.periodEnd),
  });

  return (
    <div ref={ref} className="month-chart" style={{ height }}>
      {layout && (
        <svg width={width} height={height} aria-hidden="true">
          <g transform={`translate(${margin.left},${margin.top})`}>
            <line x1={0} x2={innerW} y1={innerH} y2={innerH} className="chart__gridline" />
            {layout.dayTicks.map((d, i) => (
              <text key={d} x={layout.x(d)} y={innerH + 15} textAnchor={i === 0 ? 'start' : i === layout.dayTicks.length - 1 ? 'end' : 'middle'} className="chart__axis-label month-chart__tick">
                {dayMonth(d)}
              </text>
            ))}
            <path d={layout.areaPath} className="month-chart__area" />
            <g className="chart__reveal">
              <path d={layout.confirmedPath} className="month-chart__line" />
              <path d={layout.projectedPath} className="month-chart__line month-chart__line--projected" />
            </g>
            <line x1={layout.todayX} x2={layout.todayX} y1={-4} y2={innerH} className="month-chart__today" />
            <g transform={`translate(${clamp(layout.todayX, 26, innerW - 26)},${-margin.top + 2})`}>
              <rect x={-26} y={0} width={52} height={17} rx={8.5} className="month-chart__pill" />
              <text x={0} y={12} textAnchor="middle" className="month-chart__pill-text">{t('home.thisMonth.today')}</text>
            </g>
            {layout.dots.map(({ p, cx, cy, color }) => (
              <circle key={`${p.date}-${p.projected}`} cx={cx} cy={cy} r={3.5} fill={p.projected ? 'var(--card)' : color} stroke={color} strokeWidth={p.projected ? 2 : 1.5} />
            ))}
            <circle cx={layout.todayX} cy={layout.y(month.todayValue)} r={4.5} fill="var(--mint)" stroke="var(--card)" strokeWidth={2} />
            <circle cx={innerW} cy={layout.endY} r={4.5} fill="var(--mint)" stroke="var(--card)" strokeWidth={2} />
            {active && (
              <line x1={layout.xs[index!]} x2={layout.xs[index!]} y1={0} y2={innerH} className="chart__crosshair-line" />
            )}
          </g>
        </svg>
      )}
      {layout && (
        <ScrubOverlay
          left={margin.left} top={margin.top} width={innerW} height={innerH} label={summary} max={month.points.length - 1} index={index} handlers={handlers}
          valuetext={active ? `${dayMonth(active.date)}: ${money(active.value)}` : undefined}
        />
      )}
      {active && layout && (
        <div
          className="chart-tooltip chart-tooltip--compact"
          style={{
            left: margin.left + layout.xs[index!]! + (layout.xs[index!]! > innerW * 0.55 ? -10 : 10),
            top: 22,
            transform: layout.xs[index!]! > innerW * 0.55 ? 'translateX(-100%)' : undefined,
          }}
          aria-hidden="true"
        >
          <span className="chart-tooltip__date">
            {dayMonth(active.date)} · {active.projected ? t('home.thisMonth.projected') : t('home.thisMonth.confirmed')}
          </span>
          <span className="chart-tooltip__value">{money(active.value)}</span>
          {active.labels.slice(0, 3).map((l) => (
            <span key={l.label} className="chart-tooltip__sub">
              {l.label} {money(l.amount, { sign: true })}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
